import { useEffect, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { DroneControl } from '@/components/molecules/drone-control';
import type { DroneKey, DroneInterval, DroneOctave } from '@/lib/practice/drone-keys';

export type DroneSheetProps = {
  open: boolean;
  onClose: () => void;
  root: DroneKey | null;
  interval: DroneInterval;
  octave: DroneOctave;
  volume: number;
  on: boolean;
  onRootChange: (root: DroneKey) => void;
  onIntervalChange: (interval: DroneInterval) => void;
  onOctaveChange: (octave: DroneOctave) => void;
  onVolumeChange: (volume: number) => void;
  onOnChange: (on: boolean) => void;
  onAutoMatch?: () => void;
  autoMatchDisabled?: boolean;
};

export function DroneSheet({
  open,
  onClose,
  root,
  interval,
  octave,
  volume,
  on,
  onRootChange,
  onIntervalChange,
  onOctaveChange,
  onVolumeChange,
  onOnChange,
  onAutoMatch,
  autoMatchDisabled,
}: DroneSheetProps): ReactElement | null {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Drone"
      className="fixed inset-0 z-[70] flex items-end justify-center"
    >
      <button
        type="button"
        aria-label="Dismiss drone sheet"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div className="relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-3xl bg-[#1a1a1a] shadow-2xl sm:max-w-md sm:rounded-3xl">
        <div className="flex items-center justify-between px-5 pt-3">
          <span aria-hidden="true" className="mx-auto h-1 w-10 rounded-full bg-white/25" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-3 rounded-full p-1 text-white/55 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto">
          <DroneControl
            surface="dark"
            compact
            root={root}
            interval={interval}
            octave={octave}
            volume={volume}
            on={on}
            onRootChange={onRootChange}
            onIntervalChange={onIntervalChange}
            onOctaveChange={onOctaveChange}
            onVolumeChange={onVolumeChange}
            onOnChange={onOnChange}
            onAutoMatch={onAutoMatch}
            autoMatchDisabled={autoMatchDisabled}
          />
        </div>
      </div>
    </div>
  );
}
